import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import { formatBytes, MENTION_EVERYONE, QUICK_REACTIONS } from "@shared/protocol";
import { CloseIcon, SendIcon } from "@/components/icons";
import { can, colorOf, useApp, userById } from "@/state/store";
import { C, FONT_MONO, hexA, initialOf } from "@/theme";

/** Altura maxima do campo antes de rolar por dentro. */
const MAX_FIELD_HEIGHT = 160;

/** Opcao da lista de autocompletar de mencao. */
type MentionOption = {
  handle: string;
  name: string;
  color: string;
  avatarUrl: string | null;
};

type MentionQuery = { start: number; query: string };

function ClipGlyph(): ReactElement {
  return (
    <svg
      width={17}
      height={17}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19.5 11.5 12 19a4.6 4.6 0 0 1-6.5-6.5l7.7-7.7a3.1 3.1 0 0 1 4.4 4.4l-7.7 7.7a1.6 1.6 0 0 1-2.2-2.2l7.1-7.1" />
    </svg>
  );
}

function EmojiGlyph(): ReactElement {
  return (
    <svg
      width={17}
      height={17}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.5 14.2c.9 1.2 2.1 1.9 3.5 1.9s2.6-.7 3.5-1.9" />
      <path d="M9.3 9.6h.01 M14.7 9.6h.01" />
    </svg>
  );
}

function ReplyGlyph(): ReactElement {
  return (
    <svg
      width={13}
      height={13}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9.5 7 5 11.5 9.5 16" />
      <path d="M5 11.5h9a5 5 0 0 1 5 5V19" />
    </svg>
  );
}

/** Detecta o `@` sendo digitado imediatamente antes do cursor. */
function detectMention(value: string, caret: number): MentionQuery | null {
  const before = value.slice(0, caret);
  const found = /(?:^|[\s([{>])@([a-z0-9._-]{0,24})$/i.exec(before);
  if (!found) return null;
  return { start: caret - found[1].length - 1, query: found[1].toLowerCase() };
}

export type ComposerProps = {
  /** Texto do campo vazio, ex.: "Mensagem em #geral". */
  placeholder: string;
};

export function Composer({ placeholder }: ComposerProps): ReactElement {
  const { state, actions } = useApp();
  const fieldRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [mention, setMention] = useState<MentionQuery | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);

  const draft = state.drafts[state.activeKey] ?? "";
  const allowed = can(state, "mensagens");
  const uploading = state.uploads.some(item => !item.attachment && item.error === null);
  const ready = state.uploads.some(item => item.attachment !== null);
  const canSend = allowed && !uploading && (draft.trim().length > 0 || ready);

  const options = useMemo<MentionOption[]>(() => {
    if (!mention) return [];
    const query = mention.query;
    const list: MentionOption[] = [];
    if (can(state, "todos") && MENTION_EVERYONE.startsWith(query)) {
      list.push({
        handle: MENTION_EVERYONE,
        name: "Todos os membros",
        color: state.settings.accent,
        avatarUrl: null,
      });
    }
    for (const user of state.users) {
      if (state.me && user.id === state.me.id) continue;
      const matches =
        query.length === 0 ||
        user.handle.toLowerCase().includes(query) ||
        user.name.toLowerCase().includes(query);
      if (!matches) continue;
      list.push({
        handle: user.handle,
        name: user.name,
        color: colorOf(state, user.id),
        avatarUrl: user.avatarUrl,
      });
    }
    return list.slice(0, 8);
  }, [mention, state]);

  // O campo cresce com o texto e volta ao tamanho de uma linha ao esvaziar.
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return;
    field.style.height = "auto";
    field.style.height = `${Math.min(field.scrollHeight, MAX_FIELD_HEIGHT)}px`;
  }, [draft]);

  // Trocar de conversa fecha os popups pendentes.
  useEffect(() => {
    setMention(null);
    setEmojiOpen(false);
  }, [state.activeKey]);

  useEffect(() => {
    if (mentionIndex < options.length) return;
    setMentionIndex(0);
  }, [options.length, mentionIndex]);

  // Ao escolher responder, o foco volta para o campo.
  useEffect(() => {
    if (state.replyTo) fieldRef.current?.focus();
  }, [state.replyTo]);

  const submit = useCallback(() => {
    if (!canSend) return;
    setMention(null);
    setEmojiOpen(false);
    actions.send();
    const field = fieldRef.current;
    if (field) {
      field.style.height = "auto";
      field.focus();
    }
  }, [actions, canSend]);

  const applyMention = useCallback(
    (option: MentionOption) => {
      const field = fieldRef.current;
      if (!field || !mention) return;
      const caret = field.selectionStart ?? draft.length;
      const next = `${draft.slice(0, mention.start)}@${option.handle} ${draft.slice(caret)}`;
      const position = mention.start + option.handle.length + 2;
      setMention(null);
      actions.setDraft(next);
      window.requestAnimationFrame(() => {
        field.focus();
        field.setSelectionRange(position, position);
      });
    },
    [actions, draft, mention],
  );

  const onChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.target.value;
      const caret = event.target.selectionStart ?? value.length;
      actions.setDraft(value);
      if (value.trim().length > 0) actions.notifyTyping();
      const found = detectMention(value, caret);
      setMention(found);
      if (found) setMentionIndex(0);
    },
    [actions],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (mention && options.length > 0) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setMentionIndex(index => (index + 1) % options.length);
          return;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setMentionIndex(index => (index - 1 + options.length) % options.length);
          return;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          event.preventDefault();
          applyMention(options[mentionIndex] ?? options[0]);
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          setMention(null);
          return;
        }
      }
      if (event.key === "Escape" && state.replyTo) {
        event.preventDefault();
        actions.setReplyTo(null);
        return;
      }
      if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
        event.preventDefault();
        submit();
      }
    },
    [actions, applyMention, mention, mentionIndex, options, state.replyTo, submit],
  );

  const onPaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(event.clipboardData.files);
      if (files.length === 0) return;
      event.preventDefault();
      actions.attachFiles(files);
    },
    [actions],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      const files = Array.from(event.dataTransfer.files);
      if (files.length > 0) actions.attachFiles(files);
    },
    [actions],
  );

  const onDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!Array.from(event.dataTransfer.types).includes("Files")) return;
    dragDepth.current += 1;
    setDragging(true);
  }, []);

  const onDragLeave = useCallback(() => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  }, []);

  const onPickFiles = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      if (files.length > 0) actions.attachFiles(files);
      event.target.value = "";
    },
    [actions],
  );

  const insertEmoji = useCallback(
    (emoji: string) => {
      const field = fieldRef.current;
      const caret = field?.selectionStart ?? draft.length;
      const next = `${draft.slice(0, caret)}${emoji}${draft.slice(caret)}`;
      setEmojiOpen(false);
      actions.setDraft(next);
      window.requestAnimationFrame(() => {
        if (!field) return;
        field.focus();
        const position = caret + emoji.length;
        field.setSelectionRange(position, position);
      });
    },
    [actions, draft],
  );

  const replyAuthor = state.replyTo ? userById(state, state.replyTo.authorId) : undefined;

  return (
    <div
      onDragEnter={onDragEnter}
      onDragOver={event => event.preventDefault()}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{ flex: "0 0 auto", padding: "6px 16px 16px" }}
    >
      {state.uploads.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
          {state.uploads.map(upload => (
            <div
              key={upload.id}
              style={{
                flex: "1 1 220px",
                maxWidth: 320,
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "7px 10px",
                border: upload.error
                  ? `1px solid ${hexA(C.red, 0.55)}`
                  : `1px solid ${C.lineStrong}`,
                borderRadius: 10,
                background: C.raised,
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontFamily: FONT_MONO,
                    fontSize: 11.5,
                    color: C.textBody,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {upload.name}
                </span>
                <span
                  style={{
                    display: "block",
                    marginTop: 2,
                    fontSize: 11,
                    color: upload.error ? C.red : C.textFaint,
                  }}
                >
                  {upload.error ??
                    (upload.attachment
                      ? formatBytes(upload.size)
                      : `${Math.round(upload.progress * 100)}% · ${formatBytes(upload.size)}`)}
                </span>
                {!upload.attachment && !upload.error && (
                  <span
                    style={{
                      display: "block",
                      height: 3,
                      marginTop: 5,
                      borderRadius: 999,
                      background: C.lineStrong,
                      overflow: "hidden",
                    }}
                  >
                    <span
                      style={{
                        display: "block",
                        height: "100%",
                        width: `${Math.round(upload.progress * 100)}%`,
                        borderRadius: 999,
                        background: "var(--ac, #1F4FD8)",
                        transition: "width 140ms ease",
                      }}
                    />
                  </span>
                )}
              </span>
              <button
                type="button"
                className="hov-bg-24"
                onClick={() => actions.removeUpload(upload.id)}
                title="Remover anexo"
                aria-label="Remover anexo"
                style={{
                  width: 26,
                  height: 26,
                  flex: "0 0 auto",
                  display: "grid",
                  placeItems: "center",
                  border: 0,
                  borderRadius: 7,
                  cursor: "pointer",
                  background: "transparent",
                  color: C.textMuted,
                }}
              >
                <CloseIcon size={12} color="currentColor" />
              </button>
            </div>
          ))}
        </div>
      )}

      {state.replyTo && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
            padding: "6px 8px 6px 12px",
            border: `1px solid ${C.lineStrong}`,
            borderRadius: 10,
            background: C.raised,
          }}
        >
          <span style={{ color: C.textDim, display: "grid", placeItems: "center" }}>
            <ReplyGlyph />
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 12.5,
              color: C.textDim,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Respondendo a{" "}
            <span style={{ fontWeight: 600, color: colorOf(state, state.replyTo.authorId) }}>
              {replyAuthor?.name ?? state.replyTo.authorId}
            </span>
          </span>
          <button
            type="button"
            className="hov-bg-24"
            onClick={() => actions.setReplyTo(null)}
            title="Cancelar resposta"
            aria-label="Cancelar resposta"
            style={{
              width: 26,
              height: 26,
              flex: "0 0 auto",
              display: "grid",
              placeItems: "center",
              border: 0,
              borderRadius: 7,
              cursor: "pointer",
              background: "transparent",
              color: C.textMuted,
            }}
          >
            <CloseIcon size={12} color="currentColor" />
          </button>
        </div>
      )}

      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "6px 6px 6px 14px",
          border: dragging ? "1px dashed var(--ac, #1F4FD8)" : `1px solid ${C.lineStrong}`,
          borderRadius: 12,
          background: dragging ? hexA(C.accent, 0.06) : C.raised,
        }}
      >
        {dragging && (
          <span
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              borderRadius: 12,
              fontFamily: FONT_MONO,
              fontSize: 10,
              letterSpacing: "0.12em",
              color: "var(--ac, #1F4FD8)",
              background: C.raised,
              pointerEvents: "none",
            }}
          >
            SOLTE PARA ANEXAR
          </span>
        )}

        <input
          ref={fileRef}
          type="file"
          multiple
          onChange={onPickFiles}
          style={{ display: "none" }}
        />
        <button
          type="button"
          className="hov-bg-24-fg"
          onClick={() => fileRef.current?.click()}
          disabled={!allowed}
          title="Anexar arquivo"
          aria-label="Anexar arquivo"
          style={{
            width: 30,
            height: 30,
            flex: "0 0 auto",
            display: "grid",
            placeItems: "center",
            border: 0,
            borderRadius: 8,
            cursor: allowed ? "pointer" : "default",
            background: "transparent",
            color: C.textMuted,
            opacity: allowed ? 1 : 0.45,
          }}
        >
          <ClipGlyph />
        </button>

        <textarea
          ref={fieldRef}
          rows={1}
          value={draft}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={() => setMention(null)}
          disabled={!allowed}
          placeholder={allowed ? placeholder : "Você não pode enviar mensagens aqui."}
          style={{
            flex: 1,
            minWidth: 0,
            boxSizing: "border-box",
            border: 0,
            outline: 0,
            resize: "none",
            overflowY: "auto",
            maxHeight: MAX_FIELD_HEIGHT,
            background: "transparent",
            color: C.text,
            fontSize: 14.5,
            lineHeight: "18px",
            padding: "7px 0",
          }}
        />

        <button
          type="button"
          className="hov-bg-24-fg"
          onClick={() => setEmojiOpen(open => !open)}
          disabled={!allowed}
          title="Emoji"
          aria-label="Emoji"
          style={{
            width: 30,
            height: 30,
            flex: "0 0 auto",
            display: "grid",
            placeItems: "center",
            border: 0,
            borderRadius: 8,
            cursor: allowed ? "pointer" : "default",
            background: emojiOpen ? C.hoverStrong : "transparent",
            color: emojiOpen ? C.textStrong : C.textMuted,
            opacity: allowed ? 1 : 0.45,
          }}
        >
          <EmojiGlyph />
        </button>

        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 9,
            letterSpacing: "0.1em",
            color: C.textGhost,
            whiteSpace: "nowrap",
          }}
        >
          ENTER ENVIA
        </span>

        <button
          type="button"
          className="act-press"
          onClick={submit}
          disabled={!canSend}
          title={uploading ? "Aguardando o envio dos anexos" : "Enviar"}
          style={{
            width: 36,
            height: 36,
            flex: "0 0 auto",
            display: "grid",
            placeItems: "center",
            border: 0,
            borderRadius: 9,
            cursor: canSend ? "pointer" : "default",
            background: "var(--ac, #1F4FD8)",
            color: C.ink,
            opacity: canSend ? 1 : 0.45,
            transition: "transform 120ms ease",
          }}
        >
          <SendIcon size={16} color="currentColor" />
        </button>

        {emojiOpen && (
          <div
            style={{
              position: "absolute",
              right: 0,
              bottom: "calc(100% + 8px)",
              display: "flex",
              gap: 2,
              padding: 6,
              border: `1px solid ${C.lineStrong}`,
              borderRadius: 10,
              background: C.raised,
              boxShadow: "0 12px 30px rgba(0,0,0,.45)",
              zIndex: 20,
              animation: "pop 120ms ease",
            }}
          >
            {QUICK_REACTIONS.map(emoji => (
              <button
                key={emoji}
                type="button"
                className="hov-bg-24"
                onMouseDown={event => {
                  event.preventDefault();
                  insertEmoji(emoji);
                }}
                style={{
                  width: 30,
                  height: 30,
                  display: "grid",
                  placeItems: "center",
                  border: 0,
                  borderRadius: 8,
                  cursor: "pointer",
                  background: "transparent",
                  fontSize: 16,
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        {mention && options.length > 0 && (
          <div
            style={{
              position: "absolute",
              left: 0,
              bottom: "calc(100% + 8px)",
              width: "min(320px, 100%)",
              maxHeight: 224,
              overflowY: "auto",
              padding: 6,
              border: `1px solid ${C.lineStrong}`,
              borderRadius: 12,
              background: C.raised,
              boxShadow: "0 16px 40px rgba(0,0,0,.5)",
              zIndex: 20,
              animation: "pop 120ms ease",
            }}
          >
            <div
              style={{
                padding: "4px 9px 6px",
                fontFamily: FONT_MONO,
                fontSize: 9,
                letterSpacing: "0.12em",
                color: C.textLabel,
              }}
            >
              MENCIONAR
            </div>
            {options.map((option, index) => (
              <button
                key={option.handle}
                type="button"
                className={index === mentionIndex ? undefined : "hov-bg-24"}
                onMouseDown={event => {
                  event.preventDefault();
                  applyMention(option);
                }}
                onMouseEnter={() => setMentionIndex(index)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: "7px 9px",
                  border: 0,
                  borderRadius: 8,
                  cursor: "pointer",
                  textAlign: "left",
                  background: index === mentionIndex ? C.hoverStrong : "transparent",
                  color: "inherit",
                }}
              >
                <span
                  style={{
                    width: 22,
                    height: 22,
                    flex: "0 0 auto",
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "50%",
                    overflow: "hidden",
                    fontSize: 10.5,
                    fontWeight: 600,
                    background: option.avatarUrl ? C.lineSoft : hexA(option.color, 0.18),
                    color: option.color,
                  }}
                >
                  {option.avatarUrl ? (
                    <img
                      src={option.avatarUrl}
                      alt=""
                      style={{ width: 22, height: 22, objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    initialOf(option.name)
                  )}
                </span>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: C.textStrong,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {option.name}
                </span>
                <span style={{ fontFamily: FONT_MONO, fontSize: 11, color: C.textFaint }}>
                  @{option.handle}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
